import React from 'react';
import { ArrowLeft, Calendar, MapPin, TrendingDown, Train } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const ChinaEURailFreightArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-red-500">
              <TrendingDown className="h-3 w-3 mr-1" />
              {t('article.railfreight.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.railfreight.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.railfreight.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.railfreight.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.railfreight.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://images.unsplash.com/photo-1474487548417-781cb71495f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2084&q=80" 
            alt={t('article.railfreight.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.railfreight.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-red-500 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Train className="h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-800 mb-2">Key Highlight</h3>
                    <p className="text-red-700">{t('article.railfreight.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.railfreight.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.railfreight.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.railfreight.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.railfreight.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.railfreight.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.railfreight.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.railfreight.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.railfreight.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center text-red-800">
                  <TrendingDown className="h-5 w-5 mr-2" />
                  {t('article.railfreight.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-red-700">
                  {t('article.railfreight.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">H1 2025 Volume</span>
                  <span className="font-semibold">138,009 TEUs</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Decline</span>
                  <span className="font-semibold text-red-600">-27%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">China Share</span>
                  <span className="font-semibold">86%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge variant="destructive">Rail Decline</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Upply Market Insights - Rail Freight Analysis
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://market-insights.upply.com/en/china-eu-rail-freight-down-in-the-first-half-of-2025', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChinaEURailFreightArticle;